import { Box, Grid, IconButton, Modal, Paper, Typography, useMediaQuery, Button, Menu, Stack, Drawer } from "@mui/material";
import React, { useContext, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Image from "../../Assets/assetHelper";
import { AuthContext } from "../../Helpers/Contexts/authContext";
import { ReactChildren } from "../../interfaces/Common";
import { AuthUser } from "../../interfaces/Contexts";
import AppBreadCrumbs from "../BreadCrumbs";
import SideNavTabs from "./Tabs";
import { useStyles } from "./styles";
import MobileNavTabs from "./MobileTabs";
import NotificationModal from "./NoficationModal";
import CalendarIcon from "../../Assets/icons_calendar.svg";
import SettingIcon from "../../Assets/icons_settings.svg";
import NotificationIcon from "../../Assets/icons_notification.svg";
import logo from "../../Assets/dsu_logo.png";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useCourseContext } from "../../Helpers/Contexts/courseContext";
import routes from "../../Utils/routes";
import { getStudentAffiliation, getStudentInfo, logoutPortalSession } from "../../Utils/apiService";
import CloseIcon from '@mui/icons-material/Close';
import KenLoader from "../../GlobalComponents/KenLoader";
import { useIdleTimeout } from "../../Hooks/useIdleTimeout";
import { enqueueSnackbar } from "notistack";

const formatAcademicSession = (session: string): string => {
  if (!session) return "N/A";
  const [month, year] = session.split("-");
  const trimmedMonth = month?.trim();
  const trimmedYear = year?.trim();
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const currentMonthIndex = months.indexOf(trimmedMonth);
  if (currentMonthIndex === -1) {
    return session;
  }
  let nextMonthIndex = currentMonthIndex + 1;
  let nextYear = trimmedYear;
  if (nextMonthIndex >= 12) {
    nextMonthIndex = 0;
    nextYear = (parseInt(trimmedYear) + 1).toString();
  }
  const formattedSession = `${session.trim()}/${months[nextMonthIndex]}-${nextYear}`;
  return formattedSession;
};

const getSemesterNumber = (termReference: string): string => {
  console.log('Term Reference:', termReference);
  if (!termReference) return 'N/A';
  const match = termReference.match(/\d+/);
  console.log('Match result:', match);
  return match ? match[0] : 'N/A';
};

const AppLayout: React.FunctionComponent<ReactChildren> = ({ children }) => {
  const classes = useStyles();
  const {
    state: { user },
    dispatch,
  } = useContext(AuthContext);
  const { value: courseState, dispatch: courseDispatch } = useCourseContext();
  const { allCourses, current } = courseState;

  console.log("AppLayout - Current Course State:", courseState);
  console.log("AppLayout - All Courses:", allCourses);
  console.log("AppLayout - Current Course:", current);

  const otherCourse = allCourses?.find((course: any) => {
    return course.Id !== current?.Id;
  });
  console.log("AppLayout - Other Course:", otherCourse);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:1025px)");
  const userData = user as AuthUser;
  const userDetails = userData?.userDetails;
  const profileData = userDetails?.profilePicture || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT8oJh_Li1F9MTL485spsUw1L-cjntjj49vaCWWPZdjPoeZFH5lsCGjQlMZKwr1vNZUz6M&usqp=CAU";
  console.log("AppLayout - Profile Data:", profileData);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openNotification, setOpenNotification] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [studentInfo, setStudentInfo] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [studentProfileInfo, setStudentProfileInfo] = useState<any>(null);

  // ─── Idle Timeout: auto-logout after 5 minutes of inactivity ─────────────
  const handleIdle = useCallback(async () => {
    const sessionId = localStorage.getItem("portal_session_id");
    try {
      if (sessionId) await logoutPortalSession(sessionId);
    } catch (e) {
      console.error("SF idle logout failed:", e);
    }
    enqueueSnackbar("You have been logged out due to inactivity.", {
      variant: "warning",
      anchorOrigin: { vertical: "top", horizontal: "center" },
    });
    dispatch({ type: "LOGOUT" });
    courseDispatch({ type: "RESET" });
    setStudentInfo(null);
  }, [dispatch, courseDispatch]);

  const handleIdleWarning = useCallback(() => {
    enqueueSnackbar("You will be logged out in 1 minute due to inactivity.", {
      variant: "info",
      anchorOrigin: { vertical: "top", horizontal: "center" },
      autoHideDuration: 8000,
    });
  }, []);

  useIdleTimeout({
    idleMinutes:    5,
    warningMinutes: 1,
    onIdle:         handleIdle,
    onWarning:      handleIdleWarning,
  });
  // ─────────────────────────────────────────────────────────────────────────

  const handleCloseNotification = () => {
    setOpenNotification(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleListClick = async (path: string, index?: number) => {
    setDrawerOpen(false);
    if (path === "/login") {
      const sessionId = localStorage.getItem("portal_session_id");
      try {
        if (sessionId) {
          await logoutPortalSession(sessionId);
        }
      } catch (e) {
        console.error("SF logout failed:", e);
      }
      dispatch({ type: "LOGOUT" });
      courseDispatch({ type: "RESET" });
      setStudentInfo(null);
    }
    navigate(path);
  };

  // Get page name from URL for header
  const getPageTitle = () => {
    const path = location.pathname.split("/").filter(Boolean);
    if (!path.length || path[0] === "home") return "Home";
    return path[path.length - 1]
      .replace(/-/g, " ")
      .replace(/%20/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Check if we should show breadcrumbs
  const shouldShowBreadcrumbs = () => {
    const path = location.pathname.split("/").filter(Boolean);
    return path.length > 1 && path[0] !== "home";
  };

  const openProfileDialog = async () => {
    setProfileDialogOpen(true);
    setProfileLoading(true);
    try {
      if (courseState?.current?.Id) {
        const response = await getStudentAffiliation(courseState.current.Id);
        if (response?.data?.data) {
          setStudentInfo(response.data.data);
        } else {
          setStudentInfo(null);
        }
      }
      if (userData?.applicantId) {
        const profileRes = await getStudentInfo(userData.applicantId);
        console.log("AppLayout - Profile Response:", profileRes);
        setStudentProfileInfo(profileRes || null);
      } else {
        setStudentProfileInfo(null);
      }
    } catch (error) {
      setStudentInfo(null);
      setStudentProfileInfo(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfileDialog = () => {
    setProfileDialogOpen(false);
  };


  return (
    <section style={{ padding: isMobile ? "20px 0px" : "20px", height: "100vh" }}>
      <Modal open={openNotification} onClose={handleCloseNotification} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <NotificationModal handleClose={handleCloseNotification} />
      </Modal>

      {isMobile ? (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: 64,
              bgcolor: "#fff",
              zIndex: 1300,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
            }}
          >
            {shouldShowBreadcrumbs() ? (
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon sx={{ fontSize: 28 }} />
              </IconButton>
            ) : (
              <img src={logo} alt="logo" style={{ height: 40 }} />
            )}
            <Typography variant="h6" sx={{ flex: 1, textAlign: "center", fontWeight: 500, textTransform: "capitalize", fontSize: "16px" }}>
              {getPageTitle()}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* <IconButton onClick={() => setOpenNotification(true)}>
                <img src={NotificationIcon} alt="notification" style={{ width: 28, height: 28 }} />
              </IconButton> */}
              <IconButton onClick={() => setDrawerOpen(true)}>
                <img className={classes.profilImageMobileView} src={profileData} alt="profile" style={{ width: 40, height: 40, borderRadius: "50%" }} />
              </IconButton>
            </Box>
          </Box>

          <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: "80vw", maxWidth: 320, borderRadius: 0, zIndex: 1300, p: 0 } }}>
            <Box sx={{ pt: 0, display: "flex", flexDirection: "column", alignItems: "stretch", minHeight: "100vh", height: "100vh", position: "relative", p: 0 }}>
              <IconButton onClick={() => setDrawerOpen(false)} sx={{ alignSelf: "flex-end", mb: 2, mt: 1, mr: 1 }}>
                <span style={{ fontSize: 24 }}>&times;</span>
              </IconButton>
              <Box sx={{ display: "flex", alignItems: "center", px: 3, pb: 2, pt: 0 }}>
                <img className={classes.profilImageMobileView} src={profileData} alt="profile" style={{ width: 60, height: 60, borderRadius: "50%", marginRight: 16, objectFit: "cover" }} onClick={openProfileDialog} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0, fontSize: "14px" }}>
                    {userData?.applicantName || ""}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ borderBottom: "1px solid #EAEFFF", width: "100%", mb: 1 }} />
              <Box sx={{ width: "100%", flex: 1, overflowY: "auto" }}>
                <MobileNavTabs handleListClick={handleListClick} name="mobile" handleClose={() => setDrawerOpen(false)} />
              </Box>
              {otherCourse?.hed__Account__r?.Program_Alias__c && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "absolute",
                    bottom: 64,
                    left: 0,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: "90%",
                      bgcolor: "#F6F8FB",
                      borderRadius: "4px",
                      p: 2,
                      textAlign: "center",
                    }}
                  >
                    <Stack direction={"column"} gap={"12px"} alignItems="center">
                      <Stack direction={"column"} gap={"4px"}>
                        <Typography className={classes.courseName}>{current?.hed__Account__r?.Name}</Typography>
                        <Typography className={classes.rollNo}>{current?.Roll_Number__c}</Typography>
                      </Stack>
                      <Stack direction={"column"} gap={"24px"}>
                        <Stack flexWrap={"wrap"} direction={"row"} gap={"8px"} justifyContent={"center"}>
                          {current?.semesterDetails?.Term__r?.hed__Parent_Term__r?.Term_Reference__c && <Typography className={classes.semester}>{current?.semesterDetails?.Term__r?.hed__Parent_Term__r?.Term_Reference__c}</Typography>}
                          <Typography className={classes.status}>{current?.hed__Status__c}</Typography>
                        </Stack>
                        <Button
                          variant="outlined"
                          fullWidth
                          endIcon={<KeyboardArrowRightIcon sx={{ color: "rgba(48, 97, 255, 1)", fontSize: "20px" }} />}
                          onClick={() => {
                            courseDispatch({ type: "SWITCH_COURSE" });
                            courseDispatch({ type: "TRIGGER" });
                            setDrawerOpen(false);
                          }}
                          sx={{ background: "#F6F8FB", border: "none", fontWeight: 600 }}
                        >
                          Switch to {otherCourse?.hed__Account__r?.Program_Alias__c} profile
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              )}
              {/* Logout Button at Bottom */}
              <Box sx={{ width: "100%", position: "absolute", bottom: 0, left: 0, borderTop: "1px solid #EAEFFF", bgcolor: "#fff", p: 0 }}>
                <Button fullWidth variant="text" onClick={() => handleListClick("/login")} sx={{ color: "#193389", fontWeight: 600, py: 2 }} startIcon={<KeyboardArrowRightIcon sx={{ color: "#193389" }} />}>
                  Logout
                </Button>
              </Box>
            </Box>
          </Drawer>


          <Box
            sx={{
              pt: 8,
              pb: 2,
              px: 2,
              width: "100vw",
              minHeight: "100vh",
              bgcolor: "#f7f8fa",
              overflow: "auto",
            }}
          >
            {children}
          </Box>
        </>
      ) : (
        <Grid container columnSpacing={3} sx={{ height: "100vh" }}>
          <Grid item xs={2} md={2} lg={2}>
            <Paper className={classes.nav}>
              <Box sx={{ display: "flex", flexDirection: "column", height: "100%", alignItems: isMobile ? "center" : "" }}>
                <Box sx={{ flexShrink: 0, marginBottom: "10px" }}>
                  <img className={classes.profilImage} src={profileData} alt="profile" style={{ cursor: 'pointer' }} onClick={openProfileDialog} />
                  <Typography className={classes.profileName}>{userData?.applicantName ? (isMobile ? userData.applicantName.slice(0, 2).toUpperCase() : userData.applicantName) : ""}</Typography>
                  <Box component={"div"} className={classes.profileCard}>
                    <Stack direction={"column"} gap={"12px"}>
                      <Stack direction={"column"} gap={"4px"}>
                        <Typography className={classes.courseName}>{current?.hed__Account__r?.Name}</Typography>
                        <Typography className={classes.rollNo}>{current?.Roll_Number__c}</Typography>
                      </Stack>
                      <Stack direction={"column"} gap={"24px"}>
                        <Stack flexWrap={"wrap"} direction={"row"} gap={"8px"} justifyContent={"center"}>
                          {current?.semesterDetails?.Term__r?.hed__Parent_Term__r?.Term_Reference__c && <Typography className={classes.semester}>{current?.semesterDetails?.Term__r?.hed__Parent_Term__r?.Term_Reference__c}</Typography>}
                          <Typography className={classes.status}>{current?.hed__Status__c}</Typography>
                        </Stack>
                        {otherCourse?.hed__Account__r?.Program_Alias__c && (
                          <Button
                            variant="outlined"
                            endIcon={<KeyboardArrowRightIcon sx={{ color: "rgba(48, 97, 255, 1)", fontSize: "20px" }} />}
                            onClick={() => {
                              courseDispatch({ type: "SWITCH_COURSE" });
                              courseDispatch({ type: "TRIGGER" });
                            }}
                          >
                            Switch to {otherCourse?.hed__Account__r?.Program_Alias__c} profile
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <SideNavTabs handleListClick={handleListClick} />
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item container xs={10} md={10} columnSpacing={3} lg={10}>
            <Grid item xs={6} sm={8} md={9} lg={9} sx={{ height: "10vh" }}>
              <Paper className={classes.header} sx={{ borderRadius: "9px !important" }}>
                <AppBreadCrumbs />
              </Paper>
            </Grid>
            <Grid item container xs={6} sm={4} md={3} lg={3} sx={{ height: "10vh" }}>
              <Paper className={classes.actions}>
                <Grid container spacing={2} sx={{ height: "10vh" }} alignItems={"center"}>
                  <Grid item lg={6}>
                    <div className="d-flex align-items-center justify-content-between">
                      <IconButton onClick={() => navigate("/home/calendar")}>
                        <img src={CalendarIcon} height={"20vh"} width={"20vh"} />
                      </IconButton>
                      <IconButton onClick={openProfileDialog}>
                        <img className={classes.profilImageMobileView} src={profileData} alt="profile" style={{ width: "30px", height: "30px", borderRadius: "50%" }} />
                      </IconButton>
                    </div>
                  </Grid>
                  <Grid item lg={1} textAlign={"center"} height={"70%"}>
                    <div style={{ width: "1px", height: "100%", background: "#C6C9D2", margin: "0px 8px" }} />
                  </Grid>
                  <Grid item lg={5}>
                    <img
                      alt="logo"
                      className={classes.actionLogo}
                      src={logo}
                      onClick={() => {
                        navigate(routes.home);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {children}
          </Grid>
        </Grid>
      )}

      <Modal open={profileDialogOpen} onClose={closeProfileDialog} aria-labelledby="profile-dialog-title" aria-describedby="profile-dialog-description">
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '98vw' : 1200,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: isMobile ? 1.5 : 5,
          maxHeight: '95vh',
          overflowY: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="profile-dialog-title" sx={{ fontSize: 22, fontWeight: 600, textAlign: 'left' }}>Profile</Typography>
            <IconButton onClick={closeProfileDialog}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ height: 3, width: '100%', mb: 3, background: 'linear-gradient(270deg, rgba(191, 206, 255, 0) 0%, #BFCEFF 100%)', borderRadius: 2 }} />
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <KenLoader />
            </Box>
          ) : studentInfo ? (
            <Box>
              <Grid container spacing={4} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>ABC-ID</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>{studentProfileInfo.ABCId || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>DEB-Id</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>{studentInfo.debId || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>University Email</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left', wordBreak: 'break-word' }}>{studentInfo.DSUEmail || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Mobile number</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>{studentInfo.ContactPhone || 'N/A'}</Typography>
                </Grid>
              </Grid>
              <Grid container spacing={4} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Program Name</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>{studentInfo.programName || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>USN (roll number)</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>{studentInfo.USN || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Semester</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>
                    {getSemesterNumber(current?.semesterDetails?.Term__r?.hed__Parent_Term__r?.Term_Reference__c)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Academic Session</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>{formatAcademicSession(studentInfo.AcadamicSession) || 'N/A'}</Typography>
                </Grid>
              </Grid>
              <Grid container spacing={4} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Nationality</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>
                    {studentProfileInfo?.Nationality || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Country of residence</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left' }}>
                    {studentProfileInfo?.address?.[0]?.add?.hed__MailingCountry__c || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ color: '#8C8E94', fontSize: '16px', fontWeight: 500, mb: 0.5, textAlign: 'left' }}>Address</Typography>
                  <Typography sx={{ color: '#373A45', fontSize: '18px', fontWeight: 500, textAlign: 'left', wordBreak: 'break-word' }}>{studentInfo.MailingAddress || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={3} />
              </Grid>
            </Box>
          ) : (
            <Typography sx={{ color: '#8C8E94', textAlign: 'center', mt: 4 }}>
              No student information available
            </Typography>
          )}
        </Box>
      </Modal>
    </section>
  );
};

export default AppLayout;
